<script setup lang="ts">
import { Icon } from '@iconify/vue';
/**
 * UiTabs - Design System Tabs Component
 *
 * A tabbed interface for organizing content.
 *
 * @example
 * <UiTabs v-model="activeTab" :tabs="tabs">
 *   <template #tab-1>Content for tab 1</template>
 *   <template #tab-2>Content for tab 2</template>
 * </UiTabs>
 */
import { computed } from 'vue';

export interface TabItem {
   /** Unique identifier */
   id: string;
   /** Display label */
   label: string;
   /** Optional icon */
   icon?: string;
   /** Whether the tab is disabled */
   disabled?: boolean;
   /** Badge count */
   badge?: number;
}

export interface UiTabsProps {
   /** Array of tab definitions */
   tabs: TabItem[];
   /** Visual variant */
   variant?: 'underline' | 'pills' | 'enclosed';
   /** Size variant */
   size?: 'sm' | 'md' | 'lg';
   /** Full width tabs */
   fullWidth?: boolean;
}

const props = withDefaults(defineProps<UiTabsProps>(), {
   variant: 'underline',
   size: 'md',
   fullWidth: false,
});

const activeTab = defineModel<string>({ required: true });

const tabListClasses = computed(() => {
   const base = ['flex'];

   if (props.variant === 'underline') {
      base.push('border-b border-neutral gap-1');
   }
   else if (props.variant === 'pills') {
      base.push('gap-2 p-1 bg-neutral-light rounded-lg');
   }
   else if (props.variant === 'enclosed') {
      base.push('gap-0');
   }

   if (props.fullWidth) {
      base.push('w-full');
   }

   return base.join(' ');
});

function getTabClasses(tab: TabItem) {
   const isActive = activeTab.value === tab.id;
   const base = ['inline-flex items-center justify-center gap-2 font-medium transition-colors'];

   // Size classes
   const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-2.5 text-lg',
   };
   base.push(sizeClasses[props.size]);

   if (props.fullWidth) {
      base.push('flex-1');
   }

   if (tab.disabled) {
      base.push('opacity-50 cursor-not-allowed');
   }
   else {
      base.push('cursor-pointer');
   }

   // Variant-specific classes
   if (props.variant === 'underline') {
      base.push('-mb-px border-b-2');
      if (isActive) {
         base.push('border-primary text-primary');
      }
      else {
         base.push('border-transparent text-neutral-dark hover:text-black hover:border-neutral');
      }
   }
   else if (props.variant === 'pills') {
      base.push('rounded-md');
      if (isActive) {
         base.push('bg-white text-primary shadow-sm');
      }
      else {
         base.push('text-neutral-dark hover:text-black');
      }
   }
   else if (props.variant === 'enclosed') {
      base.push('border border-b-0 rounded-t-lg -mb-px');
      if (isActive) {
         base.push('bg-white border-neutral text-black');
      }
      else {
         base.push('bg-neutral-light border-transparent text-neutral-dark hover:text-black');
      }
   }

   return base.join(' ');
}

const iconSize = computed(() => {
   const sizes = { sm: 'size-4', md: 'size-5', lg: 'size-5' };
   return sizes[props.size];
});

function selectTab(tab: TabItem) {
   if (!tab.disabled) {
      activeTab.value = tab.id;
   }
}
</script>

<template>
   <div>
      <!-- Tab list -->
      <div :class="tabListClasses" role="tablist">
         <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            role="tab"
            :aria-selected="activeTab === tab.id"
            :aria-disabled="tab.disabled"
            :class="getTabClasses(tab)"
            @click="selectTab(tab)"
         >
            <Icon v-if="tab.icon" :icon="tab.icon" :class="iconSize" />
            <span>{{ tab.label }}</span>
            <span
               v-if="tab.badge !== undefined"
               class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-medium rounded-full"
               :class="activeTab === tab.id ? 'bg-primary/20 text-primary' : 'bg-neutral text-neutral-dark'"
            >
               {{ tab.badge }}
            </span>
         </button>
      </div>

      <!-- Tab panels -->
      <div
         class="mt-4"
         :class="{ 'border border-t-0 border-neutral rounded-b-lg p-4': variant === 'enclosed' }"
      >
         <template v-for="tab in tabs" :key="tab.id">
            <div v-if="activeTab === tab.id" role="tabpanel">
               <slot :name="tab.id" />
            </div>
         </template>
      </div>
   </div>
</template>
